import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, SearchIcon, CheckCircle2Icon, ClockIcon, MessageSquareIcon, ChevronLeftIcon, SparklesIcon, TrendingDownIcon, ThumbsUpIcon, AlertTriangleIcon, HomeIcon, SendIcon } from 'lucide-react';
import { AIResponse } from '../components/AIResponse';
import { ListingCarousel } from '../components/ListingCarousel';
import { ListingCard, ListingCardProps } from '../components/ListingCard';
// --- Mock Data for Detail View ---
const CAR_IMG_A = 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_B = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800';
const carListings: ListingCardProps[] = [{
  id: 'c1',
  title: 'Toyota Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 98,
  sellerType: 'Verified Business'
}, {
  id: 'c2',
  title: 'Toyota Prado TXL 2019',
  price: 'AED 89,000',
  location: 'Abu Shagara, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '85,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 94,
  sellerType: 'Individual'
}];
const carInsights = [{
  title: 'Average Price',
  value: 'AED 84,000',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: 'Prado GXR 2018',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: 'c1'
}];
const HOME_IMG_A = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_B = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=800';
const propertyListings: ListingCardProps[] = [{
  id: 'p1',
  title: '4BR Villa — Arabian Ranches',
  price: 'AED 3.2M',
  location: 'Arabian Ranches, Dubai',
  images: [HOME_IMG_A, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,400 sqft'
  }, {
    label: 'School',
    value: '5 min'
  }],
  matchScore: 97,
  sellerType: 'Verified Business'
}, {
  id: 'p2',
  title: '3BR Townhouse — Town Square',
  price: 'AED 1.9M',
  location: 'Town Square, Dubai',
  images: [HOME_IMG_B, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,100 sqft'
  }, {
    label: 'School',
    value: '8 min'
  }],
  matchScore: 93,
  sellerType: 'Dealer'
}];
const propertyInsights = [{
  title: 'Average Price',
  value: 'AED 2.8M',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Closest to School',
  value: 'Arabian Ranches',
  type: 'positive' as const,
  icon: HomeIcon,
  targetId: 'p1'
}];
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
  kind?: 'search' | 'narrowed' | 'reply';
};
type RequestItem = {
  id: string;
  title: string;
  category: 'cars' | 'property';
  date: string;
  status: string;
  matches: number;
  lastMessage: string;
  chatHistory: ChatMessage[];
};
const mockRequests: RequestItem[] = [{
  id: '1',
  title: 'Looking for Prado under AED 90,000',
  category: 'cars',
  date: '2 days ago',
  status: 'Active',
  matches: 27,
  lastMessage: "I've narrowed it down to just 3 strong matches that fit your preferences best.",
  chatHistory: [{
    role: 'user',
    content: 'Find me a Prado under AED 90,000'
  }, {
    role: 'ai',
    kind: 'search',
    content: 'I found 27 matching vehicles. I prioritized GCC specs, clean history, lower mileage, and trusted sellers.'
  }, {
    role: 'user',
    content: 'Preferably white or silver, under 100k km.'
  }, {
    role: 'ai',
    kind: 'narrowed',
    content: "Perfect — based on what you told me, I've narrowed it down to just 3 strong matches that fit your preferences best."
  }]
}, {
  id: '2',
  title: '3 Bedroom Villa near school',
  category: 'property',
  date: '1 week ago',
  status: 'Saved',
  matches: 18,
  lastMessage: 'I found 18 properties that match. I prioritized location and proximity to schools.',
  chatHistory: [{
    role: 'user',
    content: "Need a villa near my children's school"
  }, {
    role: 'ai',
    kind: 'search',
    content: 'I found 18 properties that match. I prioritized location, proximity to schools, community quality, and trusted listings.'
  }]
}];
export function Requests() {
  const [selectedRequest, setSelectedRequest] = useState<RequestItem | null>(null);
  const [liveChat, setLiveChat] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredRequests = mockRequests.filter(r => r.title.toLowerCase().includes(searchQuery.trim().toLowerCase()));
  const threadRef = useRef<HTMLDivElement>(null);
  const openRequest = (req: RequestItem) => {
    setSelectedRequest(req);
    setLiveChat([...req.chatHistory]);
    setChatInput('');
    setIsReplying(false);
  };
  const closeRequest = () => {
    setSelectedRequest(null);
    setLiveChat([]);
  };
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const scroll = () => el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
    const raf = requestAnimationFrame(scroll);
    const t = setTimeout(scroll, 300);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [liveChat, isReplying]);
  const sendMessage = () => {
    if (!chatInput.trim() || !selectedRequest) return;
    const text = chatInput;
    setLiveChat(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setChatInput('');
    setIsReplying(true);
    setTimeout(() => {
      setIsReplying(false);
      const reply = selectedRequest.category === 'property' ? "Good question — based on this thread, I'd still recommend the Arabian Ranches villa for the school proximity. Want me to check financing or arrange a viewing?" : 'Happy to help — from these matches, the Prado GXR 2018 remains the best value. I can pull a full inspection report or start a chat with the seller. What would you like?';
      setLiveChat(prev => [...prev, {
        role: 'ai',
        kind: 'reply',
        content: reply
      }]);
    }, 1400);
  };
  const scrollToListing = (id: string) => {
    const el = document.getElementById(`req-listing-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
      el.classList.add('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      }, 1600);
    }
  };
  return <div className="flex-1 flex flex-col h-[calc(100vh-57px)] lg:h-[calc(100vh-80px)] bg-slate-50 relative overflow-hidden" data-id="element-5926" data-mp="3ka46g6lmsguo">
      <AnimatePresence mode="wait" data-id="element-5927" data-mp="3fbszc3mq0kg5">
        {!selectedRequest ? <motion.div key="list" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} className="flex-1 overflow-y-auto p-4 lg:p-8" data-id="element-5928" data-mp="3tasmf6ul4ra7">
            <div className="max-w-5xl mx-auto w-full" data-id="element-5929" data-mp="2wmcxnczauly5">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 lg:pr-44" data-id="element-5930" data-mp="3acg9da0jyx9c">
                <div data-id="element-5931" data-mp="35kestbwcqkdi">
                  <h1 className="font-display font-bold text-3xl text-dark-navy mb-2" data-id="element-5932" data-mp="35jx02p8weoac">
                    Requests History
                  </h1>
                  <p className="text-slate-500" data-id="element-5933" data-mp="2duk5fks214rh">
                    Reopen your past searches and continue where you left off.
                  </p>
                </div>
                <button className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:border-primary-indigo hover:text-primary-indigo transition-all shadow-sm" data-id="element-5934" data-mp="23o915343hdeu">
                  <PlusIcon className="w-5 h-5" data-id="element-5935" data-mp="1yqhbkfqxa3dt" />
                  New Search
                </button>
              </div>

              {/* Search field */}
              <div className="relative mb-6" data-id="element-5936" data-mp="2mhd1t8fqcqiy">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-5937" data-mp="2rg7qus4ajbbi" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search your past requests…" className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none text-slate-800 placeholder:text-slate-400 focus:border-primary-indigo/40 transition-colors shadow-sm" data-id="element-5938" data-mp="2cj6wshymn49v" />
              </div>

              {filteredRequests.length === 0 ? <div className="text-center py-16" data-id="element-5939" data-mp="2rr1qasm9rx06">
                  <p className="text-slate-500" data-id="element-5940" data-mp="2d3gmsxlhfvrq">
                    No requests match “{searchQuery}”.
                  </p>
                </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5941" data-mp="2i0qn58aetruc">
                  {filteredRequests.map((req, idx) => <motion.div key={req.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} onClick={() => openRequest(req)} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md hover:border-primary-indigo/30 transition-all group cursor-pointer flex flex-col" data-id="element-5942" data-mp="291vg5tzjkdpz" data-mpi={idx}>
                      <div className="flex justify-between items-start mb-3" data-id="element-5943" data-mp="1ctfn2nile2n9">
                        <div className="flex items-center gap-2" data-id="element-5944" data-mp="1rlhi9u9pw7gy">
                          <div className="w-8 h-8 rounded-full bg-primary-indigo/10 flex items-center justify-center" data-id="element-5945" data-mp="1msew4779s0as">
                            <MessageSquareIcon className="w-4 h-4 text-primary-indigo" data-id="element-5946" data-mp="20j9w0tzsj728" />
                          </div>
                          <span className="text-xs font-medium text-slate-500" data-id="element-5947" data-mp="1wlc61oy74gxs">
                            {req.date}
                          </span>
                        </div>
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full" data-id="element-5948" data-mp="25kh4kv9rca9y">
                          {req.matches} matches
                        </span>
                      </div>

                      <h3 className="font-display font-bold text-lg text-dark-navy mb-2 line-clamp-1" data-id="element-5949" data-mp="b89ga0rxyrn5">
                        {req.title}
                      </h3>

                      <div className="flex items-start gap-2 bg-slate-50 rounded-xl p-3 mb-4" data-id="element-5950" data-mp="2g8278w2yll1q">
                        <SparklesIcon className="w-4 h-4 text-ai-purple flex-shrink-0 mt-0.5" data-id="element-5951" data-mp="2k82v17by78lg" />
                        <p className="text-sm text-slate-600 line-clamp-2" data-id="element-5952" data-mp="2p8qjpmcpfdci">
                          {req.lastMessage}
                        </p>
                      </div>

                      <div className="mt-auto flex items-center justify-between pt-2" data-id="element-5953" data-mp="2z5lj07gsxhru">
                        <span className="text-sm font-medium text-primary-indigo group-hover:underline" data-id="element-5954" data-mp="33x3eig022q5f">
                          Open chat →
                        </span>
                      </div>
                    </motion.div>)}
                </div>}
            </div>
          </motion.div> : <motion.div key="detail" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 20
      }} className="flex-1 flex flex-col h-full bg-white" data-id="element-5955" data-mp="3hx4hq23sm9o2">
            {/* Detail Header */}
            <div className="flex-shrink-0 border-b border-slate-100 bg-white px-4 lg:px-8 py-4 flex items-center gap-4 sticky top-0 z-20" data-id="element-5956" data-mp="3e2cxm0dacemm">
              <button onClick={closeRequest} className="p-2 -ml-2 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors" data-id="element-5957" data-mp="9mk6u2bi70rx">
                <ChevronLeftIcon className="w-6 h-6" data-id="element-5958" data-mp="ui39v6514df" />
              </button>
              <div data-id="element-5959" data-mp="sfgdk0a7rcx">
                <h2 className="font-display font-bold text-lg text-dark-navy" data-id="element-5960" data-mp="xbwc4r3pmooy">
                  {selectedRequest.title}
                </h2>
                <p className="text-xs text-slate-500" data-id="element-5961" data-mp="skmotx5mvxg8">{selectedRequest.date}</p>
              </div>
            </div>

            {/* Chat Thread */}
            <div ref={threadRef} className="flex-1 overflow-y-auto px-4 lg:px-12 py-8" data-id="element-5962" data-mp="i2hnohuy6l7a">
              <div className="max-w-4xl mx-auto space-y-8" data-id="element-5963" data-mp="43ru3c7c73no">
                {liveChat.map((msg, idx) => <div key={idx} data-id="element-5964" data-mp="853aqs88gejq" data-mpi={idx}>
                    {msg.role === 'user' ? <div className="flex justify-end mb-8" data-id="element-5965" data-mp="vtmhv4fjqla8">
                        <div className="bg-slate-100 rounded-3xl rounded-tr-sm px-6 py-4 max-w-[80%] shadow-sm" data-id="element-5966" data-mp="vw8rrapwtcvm">
                          <p className="text-slate-800 text-lg" data-id="element-5967" data-mp="n1uc371gw0bz">
                            {msg.content}
                          </p>
                        </div>
                      </div> : <div className="mb-8" data-id="element-5968" data-mp="1esiboenhsxnj">
                        <AIResponse message={msg.content} insights={msg.kind === 'search' ? selectedRequest.category === 'property' ? propertyInsights : carInsights : undefined} onInsightClick={scrollToListing} data-id="element-5969" data-mp="1ergx4079pt12" />

                        {msg.kind === 'narrowed' ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 ml-0 md:ml-14" data-id="element-5970" data-mp="2la8bn9z5upv7">
                            {(selectedRequest.category === 'property' ? propertyListings : carListings).map(listing => <div key={listing.id} data-id="element-5971" data-mp="2q937bci888wq">
                                <ListingCard {...listing} data-id="element-5972" data-mp="aiyq96kyipf6" />
                              </div>)}
                          </div> : msg.kind === 'search' ? <div className="mt-6 ml-0 md:ml-14" data-id="element-5973" data-mp="cnc4itipyr01">
                            <div className="flex items-center justify-between mb-3 pr-4" data-id="element-5974" data-mp="cmtxd1uiqsdc">
                              <span className="text-sm font-medium text-slate-500" data-id="element-5975" data-mp="hkc0gbluuddf">
                                Showing top matches
                              </span>
                            </div>
                            <ListingCarousel listings={(selectedRequest.category === 'property' ? propertyListings : carListings).map(l => ({
                    ...l,
                    id: `req-listing-${l.id}`
                  }))} data-id="element-5976" data-mp="rjkzvjzhok76" />
                          </div> : null}
                      </div>}
                  </div>)}
              </div>
            </div>

            {/* Typing indicator */}
            {isReplying && <div className="flex-shrink-0 px-4 lg:px-12" data-id="element-5977" data-mp="1texkeqovkzn6">
                <div className="max-w-4xl mx-auto flex items-center gap-2 pb-2" data-id="element-5978" data-mp="1pdc7j5ccclcz">
                  <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" data-id="element-5979" data-mp="1keiufe0yuwdc" />
                  <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-5980" data-mp="1ffod6gindfvc" />
                  <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-5981" data-mp="15pmopnmdtvfw" />
                  <span className="text-sm text-slate-400 ml-1" data-id="element-5982" data-mp="wqriwcn92nd4">
                    Talab is typing…
                  </span>
                </div>
              </div>}

            {/* Active chat input — continue the conversation anytime */}
            <div className="flex-shrink-0 border-t border-slate-100 bg-white px-4 lg:px-12 py-4" data-id="element-5983" data-mp="2blhit5zljknv">
              <div className="max-w-4xl mx-auto w-full" data-id="element-5984" data-mp="2fmjkdtgzk8t0">
                <div className="w-full bg-white rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-200 flex items-center gap-2 px-4 py-2 relative group focus-within:border-primary-indigo/40 transition-colors" data-id="element-5985" data-mp="2fm08ouhxignf">
                  <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  sendMessage();
                }
              }} placeholder="Continue this conversation…" className="flex-1 bg-transparent outline-none text-slate-800 placeholder:text-slate-400" data-id="element-5986" data-mp="1rx8putq8banb" />
                  <button onClick={sendMessage} disabled={!chatInput.trim() || isReplying} className="p-2.5 bg-primary-indigo text-white rounded-full hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex-shrink-0" data-id="element-5987" data-mp="1dwo2rlda6so0">
                    <SendIcon className="w-5 h-5" data-id="element-5988" data-mp="za7kfnm6q6ym" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}